import { useState } from 'react'
import { timeAgo } from '../utils/helpers'
import ActivityDetails from './ActivityDetails'

const ICON_CLASS = { sync: 'ai-sync', product: 'ai-product', order: 'ai-order', price: 'ai-price', inventory: 'ai-inventory', conflict: 'ai-conflict', error: 'ai-error', webhook: 'ai-webhook' }
const ICON_EMOJI = { sync: '↺', product: '▣', order: '⊟', price: '$', inventory: '≡', conflict: '!', error: '✕', webhook: '⚡' }
const DOT_COLOR  = { success: 'var(--green)', warning: 'var(--amber)', error: 'var(--red)' }

/** What was done, in the seller's words rather than the code's. */
const ACTION_LABEL = {
  publish:     'Listed on eBay',
  update:      'Listing updated',
  images:      'Images updated',
  inventory:   'Stock synced',
  price:       'Price synced',
  import:      'Imported from Shopify',
  ebay_push:   'Listings pushed to eBay',
  order_sync:  'Orders fetched',
  fulfillment: 'Tracking sent',
}

export default function ActivityRow({ item, compact = false, shop }) {
  const [open, setOpen] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const meta     = item.metadata || {}
  const action   = ACTION_LABEL[meta.action]
  // Nothing to open when nothing was recorded — a disabled-looking row that
  // expands to "no detail" wastes the click.
  const hasDetail = !compact && Array.isArray(meta.items) && meta.items.length > 0
  const n = meta.total || meta.items?.length || 0

  // What failed, which is the thing this row could never say. The message has
  // always carried a count — "errors:2" — and the reasons went only to the
  // server's log, so the one entry telling a seller something was wrong was the
  // one place that could not tell them what.
  const errorNotes = Array.isArray(meta.errors) ? meta.errors : []
  const hasErrors  = !compact && errorNotes.length > 0
  const errorCount = meta.errorCount || errorNotes.length

  return (
    <div>
      <div className="activity-row">
        <div className={`activity-icon-wrap ${ICON_CLASS[item.type] || 'ai-webhook'}`}>
          <span style={{ fontSize: 13 }}>{ICON_EMOJI[item.type] || '•'}</span>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, lineHeight: 1.4 }}>{item.message}</div>
          {!compact && (
            <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              <span
                className={`badge ${item.platform === 'shopify' ? 'badge-sh' : item.platform === 'ebay' ? 'badge-eb' : 'badge-gray'}`}
                style={{ fontSize: 10 }}
              >
                {item.platform}
              </span>
              <span
                className={`badge ${item.status === 'success' ? 'badge-green' : item.status === 'warning' ? 'badge-amber' : 'badge-red'}`}
                style={{ fontSize: 10 }}
              >
                {item.status}
              </span>
              {action && (
                <span className="badge badge-gray" style={{ fontSize: 10 }}>{action}</span>
              )}
              {hasDetail && (
                <button
                  onClick={() => setOpen(o => !o)}
                  aria-expanded={open}
                  style={{ background:'none', border:'none', padding:0, cursor:'pointer',
                           color:'var(--accent)', fontSize:11, fontWeight:600, fontFamily:'inherit' }}
                >
                  {open ? 'Hide details' : `See what was synced${n > 1 ? ` (${n})` : ''}`}
                </button>
              )}
              {hasErrors && (
                <button
                  onClick={() => setShowErrors(o => !o)}
                  aria-expanded={showErrors}
                  style={{ background:'none', border:'none', padding:0, cursor:'pointer',
                           color:'var(--red)', fontSize:11, fontWeight:600, fontFamily:'inherit' }}
                >
                  {showErrors ? 'Hide what failed' : `See what failed${errorCount > 1 ? ` (${errorCount})` : ''}`}
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>
            {timeAgo(item.created_at || item.createdAt)}
          </div>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: DOT_COLOR[item.status] || 'var(--green)', marginLeft: 'auto', marginTop: 4 }} />
        </div>
      </div>

      {open && (
        <div style={{ padding: '0 14px 12px 52px' }}>
          <ActivityDetails item={item} shop={shop}/>
        </div>
      )}

      {showErrors && (
        <div style={{ padding: '0 14px 12px 52px' }}>
          {/* Grouped by stage, because fifteen failures from one pass are one
              problem to fix and read as fifteen when listed flat. */}
          {Object.entries(errorNotes.reduce((by, n) => {
            (by[n.stage || 'sync'] ||= []).push(n); return by
          }, {})).map(([stage, notes]) => (
            <div key={stage} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 650, fontFamily: 'var(--mono)',
                            color: 'var(--text2)', marginBottom: 4 }}>
                {stage} · {notes.length}
              </div>
              {notes.slice(0, 20).map((note, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5,
                                      paddingLeft: 10, borderLeft: '2px solid var(--red)',
                                      marginBottom: 3 }}>
                  {note.context && (
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>
                      {note.context}{' — '}
                    </span>
                  )}
                  {note.message}
                </div>
              ))}
              {notes.length > 20 && (
                <div style={{ fontSize: 11, color: 'var(--text3)', paddingLeft: 10 }}>
                  …and {notes.length - 20} more like this
                </div>
              )}
            </div>
          ))}
          {/* `errorsTruncated`, not `truncated`. The details panel reads
              `meta.truncated` as "how many items were left out" and prints the
              number, so one entry carrying both an item list and errors made
              these two mean different things in the same object. */}
          {meta.errorsTruncated && (
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              Showing {errorNotes.length} of {errorCount}. The rest were the same kinds of
              failure and are in the server log.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
